import { useState } from 'react'
import Dropzone, { type FileError, type FileRejection } from 'react-dropzone'
import { useCreateResultsMutation } from '../hooks/useResult'
import {
  Mtds,
  ScreenHeader,
  TopBar,
  AppLayout,
  UploadIcon,
  toSaveErrorMessage,
  XIcon
} from 'prove-common'
import FileItem from '../components/file-item/FileItem'
import { FileList } from '../components/file-list/FileList'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'

type UploadFileRejection = Omit<FileRejection, 'file'> & { file: File }

export default function UploadReportsScreen() {
  const createResults = useCreateResultsMutation()
  const [addedFiles, setAddedFiles] = useState<File[]>([])
  const [rejectedFiles, setRejectedFiles] = useState<UploadFileRejection[]>([])
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [uploadErrorFiles, setUploadErrorFiles] = useState<
    UploadFileRejection[]
  >([])

  const uploadFile = async (addedFile: File): Promise<void> => {
    setUploadErrorFiles((files) =>
      files.filter(({ file }) => file.name !== addedFile.name)
    )
    try {
      await createResults.mutateAsync({
        file: addedFile,
        fileName: addedFile.name
      })
      Mtds.toast.success(
        `Rapport mottatt, venter på sikkerhetskontroll: ${addedFile.name}`
      )
      setUploadedFiles((prevState) => [...prevState, addedFile])
    } catch (error) {
      const fileRejection: UploadFileRejection = {
        file: addedFile,
        errors: [
          {
            code: 'file-upload-error',
            message: toSaveErrorMessage(error, 'upload the report', {
              locale: 'en'
            })
          }
        ]
      }
      setUploadErrorFiles((prevState) => [...prevState, fileRejection])
    } finally {
      setUploadingFiles((prevState) =>
        prevState.filter((file) => file.name !== addedFile.name)
      )
    }
  }

  const removeAddedFile = (fileName: string): void => {
    setAddedFiles((prevState) =>
      prevState.filter((file) => file.name !== fileName)
    )
  }

  const handleUploadFiles = (): void => {
    setUploadingFiles((files) => [...files, ...addedFiles])
    addedFiles.forEach(uploadFile)
    setAddedFiles([])
  }
  const customValidator = (file: File): FileError | FileError[] | null => {
    const isDuplicate: boolean = [
      ...addedFiles,
      ...uploadingFiles,
      ...uploadedFiles,
      ...uploadErrorFiles.map(({ file }) => file)
    ].some((addedFile) => file.name === addedFile.name)
    if (isDuplicate) {
      return {
        code: 'duplicates-not-allowed',
        message: 'This file has already been added'
      }
    }
    return null
  }
  return (
    <AppLayout
      sidebar={<SideBarLayout />}
      topBar={
        <TopBar
          logo={<AppLogo />}
          title={'Upload reports'}
          showNotificationBell={true}
          isProvePlan={false}
        />
      }
    >
      <ScreenHeader title='Upload multiple reports' />
      <Mtds.Alert>
        <p>The file name needs to be structured according to this example:</p>
        <strong>0123456789_ADDRESSDESC_123456_123456_123456.pdf</strong>
      </Mtds.Alert>
      <Dropzone
        accept={{ 'application/pdf': ['.pdf'] }}
        validator={customValidator}
        onDrop={(acceptedFiles, fileRejections) => {
          setAddedFiles((prevState) => [...prevState, ...acceptedFiles])
          setRejectedFiles(fileRejections)
        }}
      >
        {({ getRootProps, getInputProps, isDragActive }) => {
          let classNames = 'flex justify-center h-32'
          if (isDragActive) classNames += ' border-border-strong'
          return (
            <Mtds.Fileupload {...getRootProps({ className: classNames })}>
              <p>Drag and drop files here, or click to add files</p>
              <input {...getInputProps()} />
            </Mtds.Fileupload>
          )
        }}
      </Dropzone>
      {!!rejectedFiles.length && (
        <FileList title='Rejected files'>
          {rejectedFiles.map((rejectedFile) => (
            <FileItem
              key={rejectedFile.file.name}
              fileName={rejectedFile.file.name}
              errors={rejectedFile.errors}
            />
          ))}
        </FileList>
      )}
      {!!addedFiles.length && (
        <FileList title='Added files'>
          {addedFiles.map((addedFile) => (
            <FileItem
              key={addedFile.name}
              fileName={addedFile.name}
              rightElement={
                <Mtds.Button
                  data-variant={'secondary'}
                  aria-label='Remove file'
                  onClick={() => {
                    removeAddedFile(addedFile.name)
                  }}
                >
                  <XIcon />
                  Remove
                </Mtds.Button>
              }
            />
          ))}
          <Mtds.Button
            data-variant={'primary'}
            onClick={handleUploadFiles}
            className={'mt-4'}
          >
            <UploadIcon />
            Upload added files
          </Mtds.Button>
        </FileList>
      )}
      {!!uploadingFiles.length && (
        <FileList title='Uploading files'>
          {uploadingFiles.map((file) => (
            <FileItem
              key={file.name}
              fileName={file.name}
              rightElement={<Mtds.Spinner />}
            />
          ))}
        </FileList>
      )}
      {!!uploadedFiles.length && (
        <FileList title='Rapporter mottatt – venter på sikkerhetskontroll'>
          {uploadedFiles.map((file) => (
            <FileItem
              key={file.name}
              fileName={file.name}
              rightElement={
                <Mtds.Validation data-color={'success'}>
                  Mottatt, venter på sikkerhetskontroll
                </Mtds.Validation>
              }
            />
          ))}
        </FileList>
      )}
      {!!uploadErrorFiles.length && (
        <FileList title='Files failed to upload'>
          {uploadErrorFiles.map((rejectedFile) => (
            <FileItem
              key={rejectedFile.file.name}
              fileName={rejectedFile.file.name}
              errors={rejectedFile.errors}
              rightElement={
                <Mtds.Flex data-gap='2'>
                  <Mtds.Button
                    data-variant='secondary'
                    onClick={() => {
                      setUploadingFiles((files) => [
                        ...files,
                        rejectedFile.file
                      ])
                      void uploadFile(rejectedFile.file)
                    }}
                  >
                    Retry upload
                  </Mtds.Button>
                  <Mtds.Button
                    data-variant='secondary'
                    aria-label='Remove failed file'
                    onClick={() =>
                      setUploadErrorFiles((files) =>
                        files.filter(
                          ({ file }) => file.name !== rejectedFile.file.name
                        )
                      )
                    }
                  >
                    <XIcon />
                    Remove
                  </Mtds.Button>
                </Mtds.Flex>
              }
            />
          ))}
        </FileList>
      )}
    </AppLayout>
  )
}
